/**
 * 案件アサインの貼り付けテキストを行へパースする（純関数）。
 *
 * 1 行 = 1 案件で、区切りは **タブ / カンマ / 半角スペース / 全角スペース** のいずれでもよい。
 * スプレッドシートからコピーするとタブ、手打ちだとスペース、CSV を貼るとカンマになるため、
 * 利用者に区切りを覚えさせない。空行は読み飛ばす（貼り付けの末尾改行で 1 行失敗にしない）。
 *
 * パース結果はプレビュー表示にも使うので、**不正な行も捨てずに `error` を持たせて返す**
 * （黙って減ると「貼ったのに件数が足りない」が気づかれないまま送信される）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件アサインの複数行入力をタブ/カンマ/スペース区切りでパースし、行ごとの検証結果を返す
 * @graph-connects none
 */

import type {
  AssignKind,
  AssignRow,
  AssignSkippedArtifact,
} from '../../../types/project-assign.js';

/** パースした 1 行。`error` が入っている行は送信対象から外す。 @graph-connects none */
export interface ParsedAssignRow {
  /** 入力テキスト上の行番号（1 始まり。空行も数える = 画面と一致させる）。 */
  lineNumber: number;
  /** 元の行テキスト（エラー表示で見せる）。 */
  raw: string;
  /** 正規化した課題キー（不正なら入力値のまま）。 */
  issueKey: string;
  /** 担当エンジニアの nickname。 */
  engineerNickname: string;
  /** レビュアーの nickname（開発アサインのみ）。 */
  reviewerNickname: string;
  /** 行が不正な場合の理由。 */
  error?: string;
}

/** 区切り文字（タブ / カンマ / 半角スペース / 全角スペース の連続を 1 区切りとして扱う）。 @graph-connects none */
const SEPARATOR = /[\t,、　 ]+/;

/**
 * 案件番号を課題キーへ正規化する。`150123` → `AIRCLOSET-150123`。
 * サーバー側の `normalizeIssueKey` と同じ規則（画面でプレビューを出すため写している。
 * 判定の正本はサーバー側で、ここは送信前に気づかせるための先出し）。
 * @graph-connects none
 */
export function normalizeIssueKeyForPreview(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (/^\d+$/.test(trimmed)) return `AIRCLOSET-${trimmed}`;
  const upper = trimmed.toUpperCase();
  return /^AIRCLOSET-\d+$/.test(upper) ? upper : null;
}

/**
 * 貼り付けテキストを行へパースする。
 *
 * `kind` を見るのは列数の期待値が違うからで、開発アサインは reviewer の列が要る。
 * 要件定義アサインで 3 列目が来ても無視せずエラーにする（列ズレを黙って通すと、
 * engineer のつもりで reviewer を担当者に立ててしまう）。
 * @graph-connects none
 */
export function parseAssignRows(text: string, kind: AssignKind): ParsedAssignRow[] {
  const expectedColumns = kind === 'development' ? 3 : 2;
  return text
    .split('\n')
    .map((raw, index) => ({ raw, lineNumber: index + 1 }))
    .filter(({ raw }) => raw.trim() !== '')
    .map(({ raw, lineNumber }) => {
      const cells = raw
        .trim()
        .split(SEPARATOR)
        .filter((cell) => cell !== '');
      const base = {
        lineNumber,
        raw,
        issueKey: cells[0] ?? '',
        engineerNickname: cells[1] ?? '',
        reviewerNickname: cells[2] ?? '',
      };
      if (cells.length < expectedColumns) {
        return {
          ...base,
          error:
            kind === 'development'
              ? '列が足りません（案件番号 / 担当エンジニア / レビュアー の 3 列）'
              : '列が足りません（案件番号 / 担当エンジニア の 2 列）',
        };
      }
      if (cells.length > expectedColumns) {
        return {
          ...base,
          error: `列が多すぎます（${String(expectedColumns)} 列で入力してください）`,
        };
      }
      const normalized = normalizeIssueKeyForPreview(base.issueKey);
      if (normalized === null) {
        return { ...base, error: '案件番号の形式が不正です（例: 150123 / AIRCLOSET-150123）' };
      }
      return { ...base, issueKey: normalized };
    });
}

/**
 * パース結果のうち送信できる行を API のペイロード形へ変換する。
 *
 * 「作らないと決めた成果物」は**案件ごと**に付く（初期値は AI の仮判断で、案件ごとに人が
 * 直せる）。要件定義アサインのときだけ乗せ、1 件も無い案件には付けない
 * （「全部作る」は既定なので、空配列を送る意味が無い）。
 * @graph-connects none
 */
export function toAssignRows(input: {
  parsed: readonly ParsedAssignRow[];
  kind: AssignKind;
  scopeByKey?: Readonly<Record<string, readonly AssignSkippedArtifact[]>>;
  /**
   * 案件ごとの AI 仮判断。id だけを送り返す（仮判断の中身は送らない）。
   *
   * **宣言を変えたかどうかに関係なく送る**——人が直した場合も「何をどう直したか」を残すには、
   * そのとき見ていた仮判断が分かる必要がある。
   */
  suggestionByKey?: Readonly<Record<string, { suggestionId?: string }>>;
}): AssignRow[] {
  const { parsed, kind, scopeByKey = {}, suggestionByKey = {} } = input;
  return parsed
    .filter((row) => row.error === undefined)
    .map((row) => {
      const requirement = kind === 'requirement';
      const skipped = requirement ? (scopeByKey[row.issueKey] ?? []) : [];
      const suggestionId = requirement ? suggestionByKey[row.issueKey]?.suggestionId : undefined;
      return {
        issueKey: row.issueKey,
        engineerNickname: row.engineerNickname,
        ...(kind === 'development' ? { reviewerNickname: row.reviewerNickname } : {}),
        ...(skipped.length > 0 ? { skipped: [...skipped] } : {}),
        ...(suggestionId === undefined ? {} : { scopeSuggestionId: suggestionId }),
      };
    });
}
